import { Layers } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { ScopePicker } from "@/components/layout/ScopePicker";
import { useDealCaptureOverview } from "@/hooks/useDealCapture";
import { AREAS, areaLabel, areaPath, locate } from "@/lib/deal-capture/paths";
import { formatNumber } from "@/lib/format";
import type { SidebarSlotProps } from "@/lib/module-registry";
import type { DealCaptureArea } from "@/types/deal-capture";

/**
 * Deal Capture's Area picker: Deals, Contracts, Entities or Deleted Deals,
 * each with its count. Choosing one opens that area's Summary, which leaves
 * any deal, contract or entity behind: the area is read from the url, so a
 * new area starts with nothing in scope.
 */
export function AreaPicker({ onClose }: SidebarSlotProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data } = useDealCaptureOverview();
  const { area } = locate(pathname);

  return (
    <ScopePicker
      axis="Area"
      icon={Layers}
      selected={area ? { id: area, label: areaLabel(area) } : null}
      placeholder="Select an area"
      searchPlaceholder="Search areas..."
      emptyMessage="No areas match."
      options={AREAS.map((a) => ({
        id: a.key,
        label: a.label,
        meta: data ? formatNumber(data.counts[a.key]) : undefined,
      }))}
      onSelect={(id) => {
        navigate(areaPath(id as DealCaptureArea));
        onClose();
      }}
    />
  );
}
